import type { StrictOmit } from '@enmon/commons';
import type {
  AuditLogAction,
  Collection,
  CountableApiResponse,
  CustomerImplementationStats,
  CustomerMe,
  SerializedAuditLog,
  SerializedCustomer,
  SerializedUser,
  ViewCustomers,
} from '@enmon/commons/lib/enmon';
import type { Commodity } from '@enmon/commons/lib/shared';
import { getQuery } from '@/components/filter/helpers';
import type { Pagination } from '@/hooks/usePagination';
import { BaseApiClient } from '@/services/baseApiClient';
import type { RequestOptions } from '@/utils/request';
import type { GetCustomerMeResult } from './customers/types/results';

export type PopulatedCustomer = StrictOmit<SerializedCustomer, 'parent'> & {
  parent?: Pick<PopulatedCustomer, '_id' | 'name'>;
};

export type PopulatedAuditLog = StrictOmit<SerializedAuditLog, 'userId'> & {
  userId: {
    _id: SerializedUser['_id'];
    email: SerializedUser['email'];
    name: SerializedUser['name'];
  };
};

export type CustomerDefaultView = PopulatedCustomer;

export type CustomerMetadataView = Pick<SerializedCustomer, '_id' | 'name'>;

export type CustomerViewResponse<TView> = TView extends ViewCustomers.Default
  ? CustomerDefaultView[]
  : CustomerMetadataView[];

export interface ActivityFilterValues extends Pagination {
  user?: SerializedUser['_id'];
  action?: AuditLogAction;
  collections?: Collection[];
  collectionDocumentId?: string;
}

export interface CustomersFilter extends Pagination {
  customer?: SerializedCustomer['_id'];
  isParent?: boolean;
  commodities?: Commodity | Commodity[];
  view?: ViewCustomers;
}

// Argument types
export type GetCustomersArg = CustomersFilter;

// Result types
export type GetCustomerResult = SerializedCustomer | null;
export type GetCustomerConfigResult = CustomerMe['config'] | null;
export type GetCustomersResult<TView> = CountableApiResponse<CustomerViewResponse<TView>> | null;
export type GetCustomerStatsResult = CustomerImplementationStats | null;

export class CustomersApiClient extends BaseApiClient {
  constructor() {
    super('api/customers');
  }

  public getOne(customerId: SerializedCustomer['_id'], signal: RequestOptions['signal']): Promise<GetCustomerResult> {
    return this.http.get(customerId, { signal });
  }

  public getMe(signal: RequestOptions['signal']): Promise<GetCustomerMeResult> {
    return this.http.get('me', { signal });
  }

  public getConfig(
    customerId: SerializedCustomer['_id'],
    signal: RequestOptions['signal'],
  ): Promise<GetCustomerConfigResult> {
    return this.http.get(`${customerId}/config`, { signal });
  }

  public get<TView extends ViewCustomers = ViewCustomers.Default>(
    filter: CustomersFilter,
    view: TView,
    signal: RequestOptions['signal'],
  ): Promise<GetCustomersResult<TView>> {
    return this.http.get('', {
      query: getQuery({ ...filter, view }),
      signal,
    });
  }

  public getAuditLogs(
    customerId: SerializedCustomer['_id'],
    filter: ActivityFilterValues,
    signal: RequestOptions['signal'],
  ): Promise<CountableApiResponse<PopulatedAuditLog[]> | null> {
    return this.http.get(`${customerId}/audit-logs`, {
      query: getQuery(filter),
      signal,
    });
  }

  public getStats(
    customerId: SerializedCustomer['_id'],
    signal: AbortSignal | undefined,
  ): Promise<GetCustomerStatsResult> {
    return this.http.get(`${customerId}/implementation-stats`, { signal });
  }
}
